import type { KeyboardEvent } from "react";

import type { IAutocompleteProps } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";

import { StyledAutocomplete } from "app/components/React/QuickSearch/styles";
import type { OnSearchItemClick } from "app/pages/activation/components/HomeSearch/types";

import { AUTOCOMPLETE_DEBOUNCE } from "../QuickSearch/config";

import { useSearchAutocompleteWithTabs } from "./useSearchAutocompleteWithTabs";

type AutocompleteWithTabsSchema = {
    autocompleteProps?: Partial<IAutocompleteProps>;
    isOpen: boolean;
    onClose: () => void;
    onKeywordClick?: OnSearchItemClick;
    onWebsiteClick?: OnSearchItemClick;
    onReportClick?: OnSearchItemClick;
    onTabClick?: (tab: string) => void;
    onLiveSearchQueryChange?: (query: string) => void;
    onTrackUserQuery?: (query: string, tab: string) => void;
    tabPanelScrollMaxHeightPx: number;
    /** Min height (px) of the scroll region. When equal to max, the panel has a fixed height. */
    tabPanelScrollMinHeightPx?: number;
};

export const SearchAutocompleteWithTabs = ({
    autocompleteProps = {},
    isOpen,
    onClose,
    onKeywordClick,
    onWebsiteClick,
    onReportClick,
    onTabClick,
    onLiveSearchQueryChange,
    onTrackUserQuery,
    tabPanelScrollMaxHeightPx,
    tabPanelScrollMinHeightPx,
}: AutocompleteWithTabsSchema) => {
    const {
        isRecentReady,
        isWsLoading,
        isKwLoading,
        isAllLoading,
        recentsVersion,
        getPlaceholder,
        getDataRenderItemsTabs,
        renderItems,
    } = useSearchAutocompleteWithTabs({
        isOpen,
        onClose,
        onKeywordClick,
        onWebsiteClick,
        onReportClick,
        onTabClick,
        onTrackUserQuery,
        tabPanelScrollMaxHeightPx,
        tabPanelScrollMinHeightPx,
    });

    if (!isRecentReady || isWsLoading || isKwLoading || isAllLoading) {
        return <DotsLoader />;
    }
    return (
        <StyledAutocomplete
            key={recentsVersion}
            className="SearchAutocompleteWithTabs"
            getListItems={getDataRenderItemsTabs}
            renderItems={renderItems}
            debounce={AUTOCOMPLETE_DEBOUNCE}
            placeholder={getPlaceholder()}
            floating
            preventTruncateUnlessForced
            autoFocus
            isFocused
            {...autocompleteProps}
            onKeyUp={(event: KeyboardEvent<HTMLInputElement>) => {
                autocompleteProps.onKeyUp?.(event);
                onLiveSearchQueryChange?.(String((event.target as HTMLInputElement | null)?.value ?? ""));
            }}
        />
    );
};
